import React, { useState } from 'react';
import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from '../../app/LanguageContext';
import {
  formatNumberAsCurrencyMask,
  handleCurrencyInputChange,
} from '../../app/currency';

const nf = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

export const newVariableCostRow = () => ({
  id: `vc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  name: '',
  cost_unit: 0,
});

const selectOnFocus = (e) => {
  const el = e.target;
  queueMicrotask(() => {
    if (el && typeof el.select === 'function') el.select();
  });
};

function VariableCostCurrencyField({ value, onChange, label, size = 'small', sx, disabled }) {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState('');
  const num = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  const shown = focused ? text : formatNumberAsCurrencyMask(num);

  return (
    <TextField
      size={size}
      fullWidth
      label={label}
      value={shown}
      disabled={disabled}
      onFocus={(e) => {
        setFocused(true);
        setText(formatNumberAsCurrencyMask(num));
        selectOnFocus(e);
      }}
      onBlur={() => {
        setFocused(false);
        setText('');
      }}
      onChange={(e) => {
        handleCurrencyInputChange(e, (synth) => {
          if (typeof synth === 'string') {
            setText('');
            onChange(0);
          } else {
            setText(synth.target.value);
            const nv = synth.target.numericValue;
            onChange(typeof nv === 'number' && Number.isFinite(nv) ? nv : 0);
          }
        });
      }}
      sx={sx}
    />
  );
}

export default function VariableCostsEditor({
  rows = [],
  onChange,
  disabled = false,
  showSectionTitle = true,
  dense = false,
}) {
  const { translate } = useTranslation();
  const list = Array.isArray(rows) ? rows : [];

  const updateRow = (id, field, value) => {
    const next = list.map((row) =>
      row.id === id
        ? { ...row, [field]: field === 'name' ? value : nf(value) }
        : row,
    );
    onChange?.(next);
  };

  const removeRow = (id) => {
    onChange?.(list.filter((row) => row.id !== id));
  };

  const addRow = () => {
    onChange?.([...list, newVariableCostRow()]);
  };

  return (
    <Box>
      {showSectionTitle ? (
        <Typography
          variant={dense ? 'caption' : 'subtitle2'}
          color={dense ? 'text.secondary' : 'text.primary'}
          sx={
            dense
              ? { textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75 }
              : { mt: dense ? 0 : 0.5, mb: 1 }
          }
        >
          {translate('price_simulate.variable_costs_section')}
        </Typography>
      ) : null}

      <Stack spacing={dense ? 0.75 : 1}>
        {list.map((row) => (
          <Stack
            key={row.id}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems="flex-end"
          >
            <TextField
              size="small"
              fullWidth
              label={translate('price_simulate.variable_cost_name')}
              value={row.name}
              disabled={disabled}
              onFocus={selectOnFocus}
              onChange={(e) => updateRow(row.id, 'name', e.target.value)}
              sx={{ flex: { xs: '1 1 auto', sm: '2 1 0' }, minWidth: 0 }}
            />
            <VariableCostCurrencyField
              size="small"
              label={translate('price_simulate.cost_unit')}
              value={row.cost_unit}
              disabled={disabled}
              onChange={(v) => updateRow(row.id, 'cost_unit', v)}
              sx={{
                flex: { xs: '1 1 auto', sm: '0 0 8.25rem' },
                minWidth: 0,
                maxWidth: { xs: 'none', sm: '9rem' },
              }}
            />
            <IconButton
              size="small"
              disabled={disabled}
              sx={{ mb: 0.5 }}
              onClick={() => removeRow(row.id)}
              aria-label={translate('common.delete')}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addRow}
        disabled={disabled}
        sx={{ mt: 1 }}
      >
        {translate('price_simulate.variable_cost_add')}
      </Button>
    </Box>
  );
}
